import React, { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Network,
  MapPin, Building2, Home, Map, FileText, CalendarDays,
  Calendar, ListTodo, Target, TrendingUp, Flag, Vote, Calculator,
  UserCog, Settings, Crown, Building, GraduationCap, Users2, Radar,
  ChevronDown, Database, Award, Landmark
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logoImg from '../../assets/logo.png';

// Role access rules: which roles can see which paths
const ALL_ROLES = ['super_admin','admin_pusat','admin_input','koordinator','saksi','ketua_dpc','dpra','bpc'];
const ADMIN_ROLES = ['super_admin','admin_pusat','admin_input'];
const KOORDINATOR_UP = ['super_admin','admin_pusat','admin_input','koordinator','ketua_dpc','dpra','bpc'];
const GRASSROOTS_ROLES = ['kader_pelopor','rki','relawan'];
const AKTIVITAS_ROLES = ['super_admin','admin_pusat','admin_input','koordinator','saksi','ketua_dpc','dpra',...GRASSROOTS_ROLES];

const menuSections = [
  // Standalone menu utama (cepat diakses tanpa buka dropdown)
  {
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ALL_ROLES },
      { to: '/peta', icon: Map, label: 'Peta Sebaran Wilayah', roles: KOORDINATOR_UP },
    ]
  },
  {
    id: 'jaringan-struktur',
    title: 'JARINGAN & STRUKTUR',
    icon: Users,
    items: [
      { to: '/partisipan', icon: Users, label: 'Data Orang (Kader)', roles: ALL_ROLES },
      { to: '/potensi-eksternal', icon: Radar, label: 'Data Tokoh & Mitra', roles: KOORDINATOR_UP },
      { to: '/struktur', icon: Network, label: 'Bagan Struktur DPD', roles: KOORDINATOR_UP },
      { to: '/pengurus-dpc', icon: Crown, label: 'Pengurus DPC', roles: KOORDINATOR_UP },
      { to: '/pengurus-dpra', icon: Building, label: 'Pengurus DPRa', roles: KOORDINATOR_UP },
      { to: '/pelopor', icon: GraduationCap, label: 'Anggota Pelopor', roles: KOORDINATOR_UP },
      { to: '/rki', icon: Users2, label: 'Anggota RKI', roles: KOORDINATOR_UP },
      { to: '/lineup-caleg', icon: Award, label: 'Lineup Caleg 2029', roles: KOORDINATOR_UP },
      { to: '/pejabat-publik', icon: Landmark, label: 'Pejabat Publik', roles: KOORDINATOR_UP },
    ]
  },
  {
    id: 'suara-teritori',
    title: 'SUARA & TERITORI',
    icon: Target,
    items: [
      { to: '/target-suara', icon: Target, label: 'Target Suara 2029', roles: KOORDINATOR_UP },
      { to: '/target-suara?tab=simulasi', icon: Calculator, label: 'Simulasi Sainte-Laguë', roles: KOORDINATOR_UP },
      { to: '/kecamatan', icon: Building2, label: 'Kecamatan', roles: KOORDINATOR_UP },
      { to: '/desa', icon: Home, label: 'Desa / Kelurahan', roles: KOORDINATOR_UP },
      { to: '/rw', icon: MapPin, label: 'Rincian RW', roles: KOORDINATOR_UP },
      { to: '/dpt', icon: Vote, label: 'Master Data DPT', roles: ['super_admin'] },
      { to: '/quick-count', icon: Vote, label: 'Quick Count / TPS', roles: ['super_admin','admin_pusat','admin_input','saksi'] },
    ]
  },
  {
    id: 'operasional',
    title: 'OPERASIONAL LAPANGAN',
    icon: CalendarDays,
    items: [
      { to: '/agenda', icon: Calendar, label: 'Agenda', roles: AKTIVITAS_ROLES },
      { to: '/kegiatan', icon: CalendarDays, label: 'Kegiatan Kampanye', roles: AKTIVITAS_ROLES },
      { to: '/tugas', icon: ListTodo, label: 'Penugasan Kader', roles: AKTIVITAS_ROLES },
      { to: '/laporan', icon: FileText, label: 'Laporan Wilayah', roles: KOORDINATOR_UP },
    ]
  },
  {
    id: 'sistem',
    title: 'PENGATURAN',
    icon: Settings,
    items: [
      { to: '/pengguna', icon: UserCog, label: 'Manajemen Pengguna', roles: ['super_admin','admin_pusat','ketua_dpc'] },
    ]
  },
];

const Sidebar = ({ collapsed = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;

  const visibleSections = useMemo(() => {
    return menuSections
      .map(section => ({ ...section, items: section.items.filter(it => !it.roles || it.roles.includes(role)) }))
      .filter(section => section.items.length > 0);
  }, [role]);

  // State dropdown open/collapse per section
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem('sipekaes_sidebar_open_sections');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { 'jaringan-struktur': true, 'suara-teritori': true };
  });

  const lastPathnameRef = useRef(null);

  // Auto-buka section HANYA saat rute berpindah (bukan saat toggle diklik secara manual)
  useEffect(() => {
    if (lastPathnameRef.current === location.pathname) return;
    lastPathnameRef.current = location.pathname;

    visibleSections.forEach(section => {
      if (!section.id) return;
      const isCurrentSection = section.items.some(it => {
        const basePath = it.to.split('?')[0];
        if (location.pathname === basePath || location.pathname.startsWith(basePath + '/')) return true;
        if (it.children?.some(ch => location.pathname === ch.to.split('?')[0])) return true;
        return false;
      });
      if (isCurrentSection) {
        setOpenSections(prev => {
          if (prev[section.id]) return prev;
          const next = { ...prev, [section.id]: true };
          try { localStorage.setItem('sipekaes_sidebar_open_sections', JSON.stringify(next)); } catch {}
          return next;
        });
      }
    });
  }, [location.pathname, visibleSections]);

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('sipekaes_sidebar_open_sections', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const handleNav = () => { if (onClose) onClose(); };

  return (
    <aside className={`${collapsed ? 'w-16' : 'w-60'} bg-white border-r border-gray-200 text-gray-700 flex flex-col h-screen sticky top-0 transition-all duration-300 z-30 select-none`}>
      <div className="px-4 py-3.5 border-b border-gray-100 cursor-pointer flex items-center gap-2.5" onClick={() => navigate('/dashboard')}>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <img src={logoImg} alt="BP3 Logo" className="w-8 h-8 object-contain" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <h1 className="text-base font-black tracking-tight leading-none text-gray-900">BP3</h1>
            <p className="text-[9px] font-bold tracking-wider mt-0.5 text-orange-600 uppercase truncate">PKS Kab. Sukabumi</p>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto no-scrollbar py-2.5 space-y-1.5">
        {visibleSections.map((section, idx) => {
          if (!section.title) {
            // Standalone menu item seperti Dashboard
            return (
              <div key={idx} className="mb-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.to} className="my-0.5">
                      <NavLink
                        to={item.to}
                        onClick={handleNav}
                        className={({ isActive }) =>
                          `group flex items-center gap-2.5 mx-2 px-3 py-1.5 rounded-lg text-[13px] transition-all relative ${
                            isActive
                              ? 'bg-orange-50 text-orange-600 font-bold border border-orange-200/80 shadow-xs'
                              : 'text-gray-600 hover:bg-gray-100/70 hover:text-gray-900 font-medium'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              className={`w-4 h-4 flex-shrink-0 transition-colors ${
                                isActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'
                              }`}
                              strokeWidth={isActive ? 2.3 : 2}
                            />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                          </>
                        )}
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            );
          }

          const SectionIcon = section.icon;
          const isOpen = !!openSections[section.id];
          const hasActiveItem = section.items.some(it => {
            const basePath = it.to.split('?')[0];
            return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
          });

          return (
            <div key={section.id || idx} className="mb-1">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className={`w-[calc(100%-16px)] mx-2 flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all text-left group ${
                    hasActiveItem
                      ? 'text-orange-950 bg-orange-50/50'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/60'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {SectionIcon && (
                      <SectionIcon
                        className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${
                          hasActiveItem ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'
                        }`}
                      />
                    )}
                    <span className="truncate uppercase tracking-wider text-[10.5px] font-bold">
                      {section.title}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-gray-100 text-gray-500 group-hover:bg-gray-200">
                      {section.items.length}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-orange-600' : 'text-gray-400'
                      }`}
                    />
                  </div>
                </button>
              )}

              {/* Tampilkan item menu saat terbuka (atau selalu tampilkan ikon jika collapsed) */}
              {(isOpen || collapsed) && (
                <div className={`space-y-0.5 ${!collapsed ? 'mt-1 pl-1' : ''}`}>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.to} className="my-0.5">
                        <NavLink
                          to={item.to}
                          onClick={handleNav}
                          className={({ isActive }) =>
                            `group flex items-center gap-2.5 mx-2 px-3 py-1.5 rounded-lg text-[13px] transition-all relative ${
                              isActive
                                ? 'bg-orange-50 text-orange-600 font-bold border border-orange-200/80 shadow-xs'
                                : 'text-gray-600 hover:bg-gray-100/70 hover:text-gray-900 font-medium'
                            }`
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <Icon
                                className={`w-4 h-4 flex-shrink-0 transition-colors ${
                                  isActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'
                                }`}
                                strokeWidth={isActive ? 2.3 : 2}
                              />
                              {!collapsed && <span className="truncate">{item.label}</span>}
                            </>
                          )}
                        </NavLink>

                        {/* Submenu anak jika ada */}
                        {item.children && !collapsed && (
                          <div className="mx-2 pl-6 pr-2 py-0.5 space-y-0.5 border-l-2 border-orange-200 ml-5 my-0.5">
                            {item.children.map(ch => (
                              <NavLink
                                key={ch.to}
                                to={ch.to}
                                onClick={handleNav}
                                className={({ isActive }) =>
                                  `flex items-center gap-2 py-1 px-2 rounded-md text-xs transition-colors ${
                                    isActive
                                      ? 'text-orange-600 font-bold bg-orange-50'
                                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'
                                  }`
                                }
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0"></span>
                                <span className="truncate">{ch.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* FOOTER BRANDING PKS KABUPATEN SUKABUMI */}
      <div className="p-3 px-4 border-t border-gray-200 bg-gray-50/70">
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-black text-gray-900 leading-none tracking-tight">PKS</p>
              <p className="text-[9px] font-bold text-gray-400 mt-0.5 uppercase tracking-wider">Kabupaten Sukabumi</p>
            </div>
            <button
              onClick={() => navigate('/pengaturan')}
              className="p-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 hover:text-gray-900 text-gray-500 transition-colors shadow-xs"
              title="Pengaturan"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="text-center font-black text-xs text-orange-600">PKS</div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
